import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { ReviewDayStatus, ReviewEnvelope, ReviewMeetingStatusInfo } from '~/services/types';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { useReviewParams } from './useReviewParams';
import { invalidateDependentReviewQueries } from './reviewQueryInvalidation';

const log = createLogger('useReviewDayStatusQuery');

export const QUERY_KEY = 'reviewDayStatus';

export type ReviewDayStatusResponse = ReviewEnvelope<ReviewMeetingStatusInfo>;

/** Lightweight status-only counterpart to `useReviewScorecards`, backing the review status
 *  banner. Polled on its own so the banner can stay fresh without re-fetching full scorecard
 *  data every few seconds. */
export function useReviewDayStatus({ date, periodId }: { date: Date; periodId: string }) {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();
  const params = useReviewParams({ date, periodId });

  const paramsQueryStr = params.toQueryString();

  const { data, error, status } = useQuery<null | ReviewDayStatusResponse>({
    queryKey: [QUERY_KEY, paramsQueryStr],
    queryFn: async () => {
      log.debug('Fetching review day status', {
        day: params.day,
        periodId: params.periodId,
        timeZone: params.timeZone ?? 'unknown',
      });
      const response = await httpClient.get<ReviewDayStatusResponse>(`/review/getDayStatus?${paramsQueryStr}`);
      log.info('Fetched review day status successfully', {
        dayStatus: response.status,
      });
      return response;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch review day status', failureCount, error, log }),
    enabled: httpClient.isAuthenticated,
    refetchInterval: (query) => {
      const dayStatus = query.state.data?.status;
      if (dayStatus === 'processing') return 3_000;
      if (dayStatus === 'active') return 5_000;
      return 30_000;
    },
  });

  // Tracks the last-observed (context, status) pair so a period/date switch — which reuses
  // this same hook instance with a different `paramsQueryStr` — is never mistaken for a real
  // status transition within one context.
  const previousRef = useRef<{ paramsQueryStr: string; dayStatus: ReviewDayStatus | null } | undefined>(undefined);
  useEffect(() => {
    if (data == null) return;
    const previous = previousRef.current;
    const isSameContext = previous?.paramsQueryStr === paramsQueryStr;
    if (isSameContext && previous.dayStatus !== data.status) {
      log.debug('Review day status changed, invalidating dependent review queries', {
        previousDayStatus: previous.dayStatus,
        dayStatus: data.status,
      });
      invalidateDependentReviewQueries(queryClient);
    }
    previousRef.current = { paramsQueryStr, dayStatus: data.status };
  }, [data, paramsQueryStr, queryClient]);

  return { data, error, status };
}
